import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {businessCaseRoutes, vehiclesRoutes} from '@dms/routes';
import {Main, queryParams, redirectFlow} from '@dms/shared';

import {composePath, isNotNilOrEmpty, useNavigate, useRequiredParams} from 'shared/web';

import {useTradeInViewState} from '../../hooks/useTradeInViewState';
import {getTradeInConfig} from '../../utils/getTradeInConfig';
import {BusinessCaseTradeInEmpty} from './components/BusinessCaseTradeInEmpty';
import {BusinessCaseTradeInLinked} from './components/BusinessCaseTradeInLinked';
import {TradeInCreateSellingDialog} from './components/TradeInCreateSellingDialog';
import {TradeInSelectCounterpartDialog} from './components/TradeInSelectCounterpartDialog';

export function BusinessCaseTradeIn() {
  const {id: businessCaseId} = useRequiredParams();
  const navigate = useNavigate();
  const [activeDialog, setActiveDialog] = useState<'link-existing' | 'create-selling' | null>(null);

  const {
    data: businessCase,
    isLoading,
    isError,
  } = useGetBusinessCaseQuery(isNotNilOrEmpty(businessCaseId) ? {businessCaseId} : skipToken);

  const viewState = useTradeInViewState(businessCase);

  const config = getTradeInConfig(businessCase?.businessCaseType);

  const handleCreateNew = () => {
    if (isNil(businessCase) || isNil(config)) {
      return;
    }
    match(config.createMode)
      .with('create-vehicle', () =>
        navigate(
          composePath(vehiclesRoutes.createV2, {
            queryParams: {
              [queryParams.REDIRECT_SOURCE]: EntityResourceIds.businessCase,
              [queryParams.REDIRECT_FLOW]: redirectFlow.tradeIn,
              [queryParams.VEHICLE_PURCHASE_TYPE]: 'IN_STOCK',
              [queryParams.CUSTOMER_ID]: businessCase.customerId,
              [queryParams.BRANCH_ID]: businessCase.branchId,
              id: businessCaseId,
              [queryParams.RETURN_URL]: composePath(businessCaseRoutes.overview, {
                params: {id: businessCaseId},
              }),
            },
          })
        )
      )
      .with('select-vehicle', () => setActiveDialog('create-selling'))
      .exhaustive();
  };

  if (isNil(businessCase) || isNil(config) || isNil(viewState)) {
    return (
      <Main>
        <DataStatus isLoading={isLoading} minHeight={60} />
      </Main>
    );
  }

  return (
    <Main>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
        {match(viewState)
          .with('EMPTY', 'EMPTY_READONLY', 'EMPTY_INVOICE_BLOCKED', (state) => (
            <BusinessCaseTradeInEmpty
              isReadOnly={state === 'EMPTY_READONLY'}
              linkExistingLabelKey={config.linkExistingLabelKey}
              createNewLabelKey={config.createNewLabelKey}
              isInvoiceBlocked={state === 'EMPTY_INVOICE_BLOCKED'}
              onLinkExisting={() => setActiveDialog('link-existing')}
              onCreateNew={handleCreateNew}
            />
          ))
          .with('LINKED_EDITABLE', 'LINKED_READONLY', (state) => (
            <BusinessCaseTradeInLinked
              businessCaseId={businessCaseId}
              linkedTradeInBusinessCaseId={businessCase.linkedTradeInBusinessCaseId ?? ''}
              isReadOnly={state === 'LINKED_READONLY'}
            />
          ))
          .exhaustive()}
        <Show when={activeDialog === 'link-existing'}>
          <TradeInSelectCounterpartDialog
            businessCaseId={businessCaseId}
            hostCustomerId={businessCase.customerId}
            hostBranchId={businessCase.branchId}
            gridCode={config.gridCode}
            titleKey={config.linkExistingLabelKey}
            onClose={() => setActiveDialog(null)}
          />
        </Show>

        <Show when={activeDialog === 'create-selling'}>
          <TradeInCreateSellingDialog
            businessCaseId={businessCaseId}
            hostCustomerId={businessCase.customerId}
            hostBranchId={businessCase.branchId}
            onClose={() => setActiveDialog(null)}
          />
        </Show>
      </DataStatus>
    </Main>
  );
}
